import React, { Component } from 'react'
import { Link, Navigate, Outlet, Route, Routes } from 'react-router-dom'
import CampaignsList from './CampaignsList'
import AudienceCampaign from './Campaign/AudienceCampaign'
import { Button } from '@demandlocal/components'
import FacebookCampaign from './Campaign/FacebookCampaign'
import DynamicDisplayCampaign from './Campaign/DynamicDisplayCampaign'
import CreateNewCampaignForm from '../../../../forms/InventoryCampaign/NewCampaign/CreateNewCampaignForm'
import loadable from '@loadable/component'
import { GA4 } from '../../../../lib/utils/ga4'
import Terms from '../../../Landing/Terms'
import mtm from '../../../../lib/utils/mtm'
import CampaignBreakdownReport from './CampaignBreakdownReport'
import CampaignCreateTypeForm from '../../../../forms/Campaigns/CampaignCreateType'
import Modal from '../../../../components/Modal/Modal'
import CampaignImportForm from '../../../../forms/Campaigns/Import'
import CampaignsSelfServiceList from './SelfService/List'
import { useLocation } from 'react-router'
import { match, P } from 'ts-pattern'
import { createHtmlPortalNode, HtmlPortalNode, OutPortal } from 'react-reverse-portal'
import SelfServiceCampaign from './SelfService/SelfServiceCampaign'
import PageHeader from '../../../../components/PageHeader/PageHeader'
import { ReactComponent as CampaignsIcon } from '../../../../images/icons/icon_chart.svg'
import TIKCampaign from './Campaign/TIKCampaign'

const OTTCampaign = loadable(() => import('./Campaign/OTTCampaign'))
const VIDCampaign = loadable(() => import('./Campaign/VIDCampaign'))
const DSLCampaign = loadable(() => import('./Campaign/DSLCampaign'))
const FDLCampaign = loadable(() => import('./Campaign/FDLCampaign'))
const VLACampaign = loadable(() => import('./Campaign/VLACampaign'))
const DSPCampaign = loadable(() => import('./Campaign/DSPCampaign'))
const MOBCampaign = loadable(() => import('./Campaign/MOBCampaign'))
const INVCampaign = loadable(() => import('./Campaign/INVCampaign'))
const SEMCampaign = loadable(() => import('./Campaign/SEMCampaign'))
const AZDCampaign = loadable(() => import('./Campaign/AZDCampaign'))
const LKNCampaign = loadable(() => import('./Campaign/LKNCampaign'))
const FDSCampaign = loadable(() => import('./Campaign/FDSCampaign'))
const AUDCampaign = loadable(() => import('./Campaign/AUDCampaign'))
const DSCCampaign = loadable(() => import('./Campaign/DSCCampaign'))
const FMLCampaign = loadable(() => import('./Campaign/FMLCampaign'))
const DOHCampaign = loadable(() => import('./Campaign/DOHCampaign'))
const PMXCampaign = loadable(() => import('./Campaign/PMXCampaign'))
const AZVCampaign = loadable(() => import('./Campaign/AZVCampaign'))
const TRVCampaign = loadable(() => import('./Campaign/TRVCampaign'))
const AVPCampaign = loadable(() => import('./Campaign/AVPCampaign'))
const LOPCampaign = loadable(() => import('./Campaign/LOPCampaign'))

const CampaignsTemplate = ({
  reportsPortalNode,
}: {
  reportsPortalNode: HtmlPortalNode<Component>
}): JSX.Element => {
  const location = useLocation()
  return (
    <div className={'flex flex-1 flex-col'}>
      <PageHeader
        tabMenu={[
          { name: 'Managed Campaigns', to: 'list' },
          { name: 'Self Service Campaigns', to: 'self-service/list' },
          { name: 'Month over Month', to: 'breakdown' },
        ]}
        title={'Campaigns'}
        rightSlot={({ hidden }) => (
          <Link
            to={match(location.pathname)
              .with(
                P.string.regex(/\/self-service\/list/),
                () => `self-service/list/create`,
              )
              .with(P.string.regex(/\/list/), () => `list/create/select`)
              .with(P.string.regex(/\/breakdown/), () => `breakdown/create`)
              .otherwise(() => '')}>
            {match(location.pathname)
              .with(P.string.regex(/\/breakdown/), () => (
                <OutPortal node={reportsPortalNode} collapsed={hidden} leftSlot={'+'} />
              ))
              .otherwise(() => (
                <Button
                  leftSlot={'+'}
                  onClick={() => {
                    GA4.sendEvent({
                      event_name: 'create_campaign',
                      event_params: {},
                    })
                    mtm.sendEvent('Create', 'Campaign')
                  }}
                  collapsed={hidden}>
                  Create or Import Campaign
                </Button>
              ))}
          </Link>
        )}
        icon={
          <div className="flex justify-center items-center rounded-full bg-black w-12 h-12">
            <CampaignsIcon className="fill-white" />
          </div>
        }
      />
      <div className={'flex flex-1 flex-col p-10 max-w-[1920px] mx-auto w-full'}>
        <Outlet />
      </div>
    </div>
  )
}

const Campaigns = (): JSX.Element => {
  const reportsPortalNode = React.useMemo(() => createHtmlPortalNode(), [])
  return (
    <Routes>
      <Route element={<CampaignsTemplate reportsPortalNode={reportsPortalNode} />}>
        {(['self-service/list', 'list', 'breakdown'] as const).map((path) => (
          <Route
            key={path}
            path={path}
            element={match(path)
              .with('self-service/list', () => <CampaignsSelfServiceList />)
              .with('list', () => <CampaignsList />)
              .with('breakdown', () => (
                <CampaignBreakdownReport reportsPortalNode={reportsPortalNode} />
              ))
              .exhaustive()}>
            <Route
              path={'create/*'}
              element={
                <Modal isOpen={true} modalRoute={true} key={'AddNewCampaign'}>
                  <Outlet />
                </Modal>
              }>
              <Route path={'import/*'} element={<CampaignImportForm />} />
              <Route path={'new'} element={<CreateNewCampaignForm />} />
              <Route path={'terms'} element={<Terms />} />
              <Route path={'select'} element={<CampaignCreateTypeForm />} />
              <Route
                path={'*'}
                element={<Navigate to="/campaigns/list/create" replace />}
              />
            </Route>
          </Route>
        ))}
        <Route path="*" element={<Navigate to="list" replace />} />
        <Route
          path={'breakdown'}
          element={<CampaignBreakdownReport reportsPortalNode={reportsPortalNode} />}
        />
      </Route>
      <Route path={'self-service/:campaign_id'} element={<SelfServiceCampaign />} />
      <Route path={'audience/:campaign_id'} element={<AudienceCampaign />} />
      <Route path={'fbd/:campaign_id'} element={<FacebookCampaign />} />
      <Route path={'dyd/:campaign_id'} element={<DynamicDisplayCampaign />} />
      <Route path={'ott/:campaign_id'} element={<OTTCampaign />} />
      <Route path={'ctv/:campaign_id'} element={<OTTCampaign />} />
      <Route path={'vid/:campaign_id'} element={<VIDCampaign />} />
      <Route path={'trv/:campaign_id'} element={<TRVCampaign />} />
      <Route path={'dsl/:campaign_id'} element={<DSLCampaign />} />
      <Route path={'fdl/:campaign_id'} element={<FDLCampaign />} />
      <Route path={'vla/:campaign_id'} element={<VLACampaign />} />
      <Route path={'dsp/:campaign_id'} element={<DSPCampaign />} />
      <Route path={'doh/:campaign_id'} element={<DOHCampaign />} />
      <Route path={'mob/:campaign_id'} element={<MOBCampaign />} />
      <Route path={'dyn/:campaign_id'} element={<FacebookCampaign />} />
      <Route path={'inv/:campaign_id'} element={<INVCampaign />} />
      <Route path={'sem/:campaign_id'} element={<SEMCampaign />} />
      <Route path={'azd/:campaign_id'} element={<AZDCampaign />} />
      <Route path={'lkn/:campaign_id'} element={<LKNCampaign />} />
      <Route path={'fds/:campaign_id'} element={<FDSCampaign />} />
      <Route path={'aud/:campaign_id'} element={<AUDCampaign />} />
      <Route path={'dsc/:campaign_id'} element={<DSCCampaign />} />
      <Route path={'mse/:campaign_id'} element={<SEMCampaign />} />
      <Route path={'miv/:campaign_id'} element={<INVCampaign />} />
      <Route path={'mvd/:campaign_id'} element={<VIDCampaign />} />
      <Route path={'azv/:campaign_id'} element={<AZVCampaign />} />
      <Route path={'fml/:campaign_id'} element={<FMLCampaign />} />
      <Route path={'pmx/:campaign_id'} element={<PMXCampaign />} />
      <Route path={'mpx/:campaign_id'} element={<PMXCampaign />} />
      <Route path={'mva/:campaign_id'} element={<VLACampaign />} />
      <Route path={'avp/:campaign_id'} element={<AVPCampaign />} />
      <Route path={'lop/:campaign_id'} element={<LOPCampaign />} />
      <Route path={'tik/:campaign_id'} element={<TIKCampaign />} />
      <Route path={'tti/:campaign_id'} element={<TIKCampaign />} />
    </Routes>
  )
}

export default Campaigns
